import { useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// Days after signup to send each email (0 = immediately on signup)
const EMAIL_SCHEDULE = [0, 3, 7, 14, 21, 28, 35];

export function useEmailNurturing() {
  const { user } = useAuth();

  const checkAndSendNurturingEmail = useCallback(async () => {
    if (!user?.id || !user?.email) return;

    try {
      // Get user creation date from metadata or profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("created_at")
        .eq("user_id", user.id)
        .single();

      if (!profile?.created_at) return;

      const createdAt = new Date(profile.created_at);
      const now = new Date();
      const daysSinceSignup = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));

      // Get last sent email sequence number from localStorage
      const lastSentKey = `nurturing_last_sent_${user.id}`;
      const lastSent = parseInt(localStorage.getItem(lastSentKey) || "-1", 10);

      // Find the next email to send
      for (let i = lastSent + 1; i < EMAIL_SCHEDULE.length; i++) {
        if (daysSinceSignup >= EMAIL_SCHEDULE[i]) {
          // Send this email
          console.log(`[NURTURING] Sending email ${i} to ${user.email}`);
          
          const { error } = await supabase.functions.invoke("send-nurturing-email", {
            body: {
              email: user.email,
              sequenceNumber: i,
              userId: user.id,
              origin: window.location.origin,
            },
          });

          if (!error) {
            localStorage.setItem(lastSentKey, String(i));
            console.log(`[NURTURING] Email ${i} sent successfully`);
          } else {
            console.error(`[NURTURING] Failed to send email ${i}:`, error);
          }

          // Only send one email per check
          break;
        }
      }
    } catch (error) {
      console.error("[NURTURING] Error in checkAndSendNurturingEmail:", error);
    }
  }, [user?.id, user?.email]);

  useEffect(() => {
    // Check on mount with a delay
    const timer = setTimeout(() => {
      checkAndSendNurturingEmail();
    }, 5000); // 5 second delay

    // Also check once per day
    const interval = setInterval(() => {
      checkAndSendNurturingEmail();
    }, 24 * 60 * 60 * 1000); // 24 hours

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [checkAndSendNurturingEmail]);
}
